import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { CENOX_HOME, nextFrom, withNext } from '../../lib/intent';
import { ApiError } from '../../lib/api';
import { Button, Input } from '../../components/ui';
import { AuthLayout } from './AuthLayout';

export function RegisterPage(): ReactNode {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const next = nextFrom(search);

  const [form, setForm] = useState({ fullName: '', email: '', password: '', phone: '' });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const update = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);

    try {
      await register({
        fullName: form.fullName,
        email: form.email,
        password: form.password,
        ...(form.phone ? { phone: form.phone } : {}),
      });
      /*
        Back to whatever they came for — /automation, /ai, /business — or
        CenoX Home if they came for CenoX itself. Never business onboarding:
        an account is a CenoX identity, and a business is something a person
        creates deliberately, from inside CenoX Business.
      */
      navigate(next ?? CENOX_HOME, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          // Surface the server's field messages inline rather than as one
          // generic banner — the user needs to know *which* input to fix.
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
        }
      } else {
        setError('Could not create your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Create your CenoX account"
      subtitle="One account for every CenoX product"
      footer={
        <>
          Already have an account?{' '}
          <Link to={withNext('/login', next)} className="font-medium text-brand-600 hover:text-brand-700">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && !Object.keys(fieldErrors).length && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <Input
          label="Full name"
          name="fullName"
          autoComplete="name"
          required
          value={form.fullName}
          onChange={update('fullName')}
          error={fieldErrors.fullName}
        />

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={update('email')}
          error={fieldErrors.email}
        />

        <Input
          label="Phone"
          type="tel"
          name="phone"
          autoComplete="tel"
          value={form.phone}
          onChange={update('phone')}
          error={fieldErrors.phone}
          hint="Optional"
        />

        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={form.password}
          onChange={update('password')}
          error={fieldErrors.password}
          hint="At least 10 characters"
        />

        <Button type="submit" size="lg" loading={submitting} className="w-full">
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
